import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  axiosGet,
  axiosPost,
  reactQueryDefaultOptions,
} from "../helpers/baseApi";
import { SERVICE_URLS } from "../helpers/serviceUrls";
import { QUERY_KEYS } from "../helpers/queryKeys";

// "Client Aetna Plan Mappings" admin screen: one Aetna medical plan mapped to up
// to three competing plans, per client, per OE period. Six endpoints - two reads,
// validate/import for the CSV workflow, copy-from-season, and remove.

// Every mapping row for one OE period, optionally narrowed to one client. Backs
// the admin grid.
export const useQueryGetClientAetnaMappingList = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_AETNA_MAPPING_LIST_GET(oePeriodId, clientId)
    );
  };

  return useQuery(
    [QUERY_KEYS.CLIENT_AETNA_MAPPING_LIST, oePeriodId, clientId],
    getData,
    {
      ...reactQueryDefaultOptions,
      enabled: !!oePeriodId,
    }
  );
};

/**
 * The mappings for one client in one OE period. Not used by the admin screen in
 * this plan - a later plan's comparison card consumes it - so the service file is
 * complete in one place rather than grown twice.
 *
 * Same shape and the same gate as useQueryGetAetnaComparePlans: `enabled` must be
 * the caller's gate, and the query also requires both a period and a client.
 */
export const useQueryGetClientAetnaMappings = (
  oePeriodId,
  clientId,
  enabled
) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_AETNA_MAPPING_CLIENT_GET(oePeriodId, clientId)
    );
  };

  return useQuery(
    [QUERY_KEYS.CLIENT_AETNA_MAPPING_CLIENT, oePeriodId, clientId],
    getData,
    {
      ...reactQueryDefaultOptions,
      enabled: Boolean(enabled) && Boolean(oePeriodId) && Boolean(clientId),
    }
  );
};

// Dry-run check of a CSV's rows before import: per-row warnings and whether each
// row will import. Changes nothing on the server, so it invalidates nothing.
export const useMutatePostClientAetnaMappingValidate = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.CLIENT_AETNA_MAPPING_VALIDATE_POST, data);
  };

  return useMutation(postData);
};

// Commits a validated CSV's rows, replacing each client's prior mappings for the
// period.
export const useMutatePostClientAetnaMappingImport = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.CLIENT_AETNA_MAPPING_IMPORT_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CLIENT_AETNA_MAPPING_LIST]);
    },
  });
};

// Copies every mapping from one OE period to another, replacing the target
// period's existing mappings.
export const useMutatePostClientAetnaMappingCopy = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.CLIENT_AETNA_MAPPING_COPY_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CLIENT_AETNA_MAPPING_LIST]);
    },
  });
};

// Removes a single mapping row.
export const useMutatePostClientAetnaMappingRemove = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.CLIENT_AETNA_MAPPING_REMOVE_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CLIENT_AETNA_MAPPING_LIST]);
    },
  });
};
